import React, { useEffect, useMemo, useRef, useState } from 'react';
import SellerLayout from './SellerLayout';
import { useSellerAuth } from '../context/SellerAuthContext';

const SellerLiveOrders = () => {
  const [shopId, setShopId] = useState(0);
  const [shopName, setShopName] = useState('');
  const [orders, setOrders] = useState([]);
  const [soundEnabled, setSoundEnabled] = useState(false);

  const lastLatestIdRef = useRef(0);
  const firstLoadedRef = useRef(false);
  const orderSoundRef = useRef(null);
  const wsRef = useRef(null);

  const { seller } = useSellerAuth();

  const status = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    return (params.get('status') || 'ALL').trim();
  }, []);

  useEffect(() => {
    orderSoundRef.current = new Audio('/sounds/order.mp3');
    orderSoundRef.current.preload = 'auto';
  }, []);

  useEffect(() => {
    const sid = parseInt(seller?.shop_id || 0, 10);
    setShopId(sid);
    setShopName(seller?.shop_name || '');
  }, [seller]);

  const getDeliveryStatusValue = (o) => {
    const ds = (o.delivery_status || '').trim();

    if (ds !== '') return ds;

    if (o.raw_status === '결제완료') return '결제완료';
    if (o.display_status === 'NEW') return '결제완료';

    return o.display_status || '결제완료';
  };

  const getDeliveryLabel = (st) => {
    if (!st || st === 'NEW') return '결제완료';
    return st;
  };

  const getDeliveryBadgeClass = (st) => {
    if (!st || st === 'NEW' || st === '결제완료') return 'bg-dark';
    if (st === '배송준비중') return 'bg-warning text-dark';
    if (st === '배송중') return 'bg-info text-dark';
    if (st === '배송완료') return 'bg-success';
    return 'bg-dark';
  };

  const getDeliveryActions = (st) => {
    if (!st || st === 'NEW' || st === '결제완료') {
      return [
        { st: '배송준비중', text: '배송준비중', class_name: 'btn-warning text-dark' },
      ];
    }

    if (st === '배송준비중') {
      return [
        { st: '배송중', text: '배송중', class_name: 'btn-info text-dark' },
      ];
    }

    if (st === '배송중') {
      return [
        { st: '배송완료', text: '배송완료', class_name: 'btn-success' },
      ];
    }

    return [];
  };

  const normalizeOrder = (o) => {
    if ((o.order_type || '') !== 'delivery') return o;

    const deliveryStatus = getDeliveryStatusValue(o);

    return {
      ...o,
      raw_status: deliveryStatus,
      display_status: deliveryStatus,
      status_label: getDeliveryLabel(deliveryStatus),
      status_badge_class: getDeliveryBadgeClass(deliveryStatus),
      is_new: deliveryStatus === '결제완료' || deliveryStatus === 'NEW',
      actions: getDeliveryActions(deliveryStatus),
    };
  };

  const playOrderVoice = () => {
    if (!soundEnabled) return;

    try {
      const audio = orderSoundRef.current;
      if (!audio) return;
      audio.pause();
      audio.currentTime = 0;
      audio.play();
    } catch (e) {
      console.error('오디오 재생 실패:', e);
    }
  };

  const enableSound = async () => {
    setSoundEnabled(true);

    try {
      const audio = orderSoundRef.current;
      if (!audio) return;

      const prevVol = audio.volume;
      audio.volume = 0.0;
      await audio.play();
      audio.pause();
      audio.currentTime = 0;
      audio.volume = prevVol;

      alert("🔔 음성 알림이 켜졌습니다! (새 주문 시 '주문이 들어왔습니다'가 나옵니다)");
    } catch (e) {
      alert(
        '소리 켜짐(일부 기기에서는 첫 재생이 제한될 수 있어요). 그래도 다음 주문 때 시도합니다.\n' +
          e.message
      );
    }
  };

  const fetchOrders = async () => {
    try {
      const res = await fetch(
        `/api/seller/kiosk/orders/list?status=${encodeURIComponent(status)}`,
        { cache: 'no-store', credentials: 'same-origin' }
      );

      const data = await res.json();

      if (!data.ok) {
        console.error('주문 목록 로드 실패');
        return;
      }

      const list = Array.isArray(data.orders) ? data.orders : [];
      setOrders(list.map(normalizeOrder));

      const latestId = parseInt(data.latest_id || 0, 10);

      if (!firstLoadedRef.current) {
        firstLoadedRef.current = true;
        lastLatestIdRef.current = latestId;
        return;
      }

      if (latestId > lastLatestIdRef.current) {
        playOrderVoice();
        lastLatestIdRef.current = latestId;
      }
    } catch (e) {
      console.error('주문 목록 로드 실패:', e);
    }
  };

  const updateDeliveryStatus = async (orderId, deliveryStatus) => {
    if (!window.confirm(`주문 #${orderId} 배송상태를 ${deliveryStatus}(으)로 변경할까요?`)) return;

    try {
      const res = await fetch('/api/seller/delivery/status-json', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          order_id: orderId,
          delivery_status: deliveryStatus,
        }),
        credentials: 'same-origin',
      });

      let data;
      try {
        data = await res.json();
      } catch (e) {
        const text = await res.text();
        alert('서버 응답이 JSON이 아닙니다:\n' + text);
        return;
      }

      if (!data.ok) {
        alert(data.msg || data.error || '배송상태 변경 실패');
        return;
      }

      fetchOrders();
    } catch (e) {
      console.error('배송상태 변경 실패:', e);
      alert('배송상태 변경 중 오류가 발생했습니다.');
    }
  };

  const updatePickupStatus = async (orderId, st) => {
    if (!window.confirm(`주문 #${orderId} 상태를 ${st}(으)로 변경할까요?`)) return;

    const res = await fetch('/api/seller/kiosk/order/update', {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: new URLSearchParams({ order_id: orderId, current_status: st }),
      credentials: 'same-origin',
    });

    let data;
    try {
      data = await res.json();
    } catch (e) {
      const text = await res.text();
      alert('서버 응답이 JSON이 아닙니다:\n' + text);
      return;
    }

    if (!data.ok) {
      alert(data.msg || '상태 변경 실패');
      return;
    }

    fetchOrders();
  };

  const setStatus = async (order, st) => {
    if ((order.order_type || '') === 'delivery') {
      await updateDeliveryStatus(order.id, st);
      return;
    }

    await updatePickupStatus(order.id, st);
  };

  useEffect(() => {
    fetchOrders();
  }, [status, soundEnabled]);

  useEffect(() => {
    if (!shopId) return;

    const proto = window.location.protocol === 'https:' ? 'wss' : 'ws';
    const ws = new WebSocket(`${proto}://${window.location.host}/ws/?shop_id=${shopId}`);
    wsRef.current = ws;

    ws.onopen = () => {
      console.log('WS 연결 성공');
    };

    ws.onmessage = (event) => {
      const msg = JSON.parse(event.data);

      if (msg.type === 'NEW_ORDER') {
        console.log('실시간 새 주문 감지', msg.order_id);
        playOrderVoice();
        fetchOrders();
      }

      if (msg.type === 'ORDER_STATUS_CHANGED') {
        fetchOrders();
      }
    };

    ws.onerror = (e) => {
      console.log('WS 오류', e);
    };

    ws.onclose = (e) => {
      console.log('WS 종료', e.code);
    };

    return () => {
      try {
        ws.close();
      } catch (e) {}
    };
  }, [shopId, soundEnabled]);

  const statusHref = (st) => `/seller/kiosk/orders?status=${encodeURIComponent(st)}`;

  return (
    <SellerLayout shopName={shopName}>
      <style>{pageCss}</style>

      <div style={styles.page}>
        <div style={styles.topRow}>
        <div
          style={{
            width: '100%',
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: '12px',
            }}
          >
            <h4 style={styles.title}>실시간 주문관리</h4>

            <button
              type="button"
              onClick={enableSound}
              style={styles.soundPill}
            >
              <span
                style={{
                  ...styles.soundDot,
                  ...(soundEnabled ? styles.soundDotOn : {}),
                }}
              />
              <span>{soundEnabled ? '소리 ON' : '소리 OFF'}</span>
            </button>
          </div>

        </div>

          <div style={styles.filterWrap}>
            <a href={statusHref('ALL')} style={styles.filterButton}>전체</a>
            <a href={statusHref('NEW')} style={styles.filterButton}>새주문</a>
            <a href={statusHref('COOKING')} style={styles.filterButton}>준비중</a>
            <a href={statusHref('DELIVERING')} style={styles.filterButton}>배송중</a>
            <a href={statusHref('DONE')} style={styles.filterButton}>완료</a>
            <a href={statusHref('CANCELLED')} style={styles.filterButton}>취소</a>
          </div>
        </div>

        {orders.length === 0 ? (
          <div style={styles.emptyBox}>주문이 없습니다.</div>
        ) : (
          orders.map((o) => (
            <div
              key={o.id}
              className={`order-card mb-4 ${o.is_new ? 'border border-2 border-danger-subtle' : ''}`}
              style={{
                ...styles.orderCard,
                ...(o.is_new ? styles.orderCardNew : {}),
              }}
            >
              <div style={styles.cardBody}>
                <div style={styles.cardHeaderRow}>
                  <div style={styles.cardHeaderLeft}>
                    <div style={styles.orderId}>#{o.id}</div>

                    <span className={`${o.status_badge_class} ${o.is_new ? 'blink' : ''}`} style={styles.badge}>
                      {o.status_label}
                    </span>

                    <span className={o.channel_badge.class_name} style={styles.badge}>
                      {o.channel_badge.label}
                    </span>

                    <span className={o.type_badge.class_name} style={styles.badge}>
                      {o.type_badge.label}
                    </span>
                  </div>

                  <div style={styles.cardHeaderRight}>
                    <div style={styles.totalPrice}>
                      {Number(o.show_total || 0).toLocaleString('ko-KR')}원
                    </div>
                  </div>
                </div>

                <div style={styles.metaBlock}>
                  <div style={styles.metaRow}>
                    <span>{o.short_dt}</span>
                    <span>·</span>
                    <span>상품 {o.item_cnt}개</span>
                  </div>

                  <div style={styles.metaRowSecond}>
                    <span>
                      {o.display_buyer_name || o.buyer_name || ''}
                    </span>
                    <span>·</span>
                    <span>{o.buyer_phone || ''}</span>
                  </div>
                </div>

                <div style={styles.bottomRow}>
                  <div style={styles.actionWrap}>
                    {Array.isArray(o.actions) &&
                      o.actions.map((a) => (
                        <button
                          key={`${o.id}-${a.st}`}
                          type="button"
                          className={a.class_name}
                          style={styles.actionButton}
                          onClick={() => setStatus(o, a.st)}
                        >
                          {a.text}
                        </button>
                      ))}
                  </div>

                  <a
                    style={styles.detailButton}
                    href={`/seller/kiosk/order/view?order_id=${encodeURIComponent(o.id)}`}
                  >
                    상세보기
                  </a>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </SellerLayout>
  );
};

const pageCss = `
body {
  background: #f6f7fb;
}

.order-card {
  border: 0;
  border-radius: 14px;
  box-shadow: 0 6px 18px rgba(0,0,0,.06);
}

.badge-soft {
  background: #eef2ff;
  color: #3949ab;
}

.blink {
  animation: blink 1s infinite;
}

@keyframes blink {
  0% { opacity: 1; }
  50% { opacity: .35; }
  100% { opacity: 1; }
}

.bg-dark {
  background: #212529;
  color: #fff;
}

.bg-warning {
  background: #ffc107;
  color: #212529;
}

.bg-info {
  background: #0dcaf0;
  color: #212529;
}

.bg-success {
  background: #198754;
  color: #fff;
}

.bg-danger {
  background: #dc3545;
  color: #fff;
}

.bg-primary {
  background: #0d6efd;
  color: #fff;
}

.bg-secondary {
  background: #6c757d;
  color: #fff;
}

.bg-light {
  background: #f8f9fa;
  color: #212529;
}

.text-dark {
  color: #212529 !important;
}

.border {
  border: 1px solid #dee2e6 !important;
}

.border-2 {
  border-width: 2px !important;
}

.border-danger-subtle {
  border-color: #f1aeb5 !important;
}

.btn-primary {
  background: #0d6efd;
  color: #fff;
  border: 1px solid #0d6efd;
}

.btn-outline-secondary {
  background: #fff;
  color: #6c757d;
  border: 1px solid #6c757d;
}

.btn-warning {
  background: #ffc107;
  color: #212529;
  border: 1px solid #ffc107;
}

.btn-info {
  background: #0dcaf0;
  color: #212529;
  border: 1px solid #0dcaf0;
}

.btn-success {
  background: #198754;
  color: #fff;
  border: 1px solid #198754;
}

.mb-3 {
  margin-bottom: 1rem;
}

@media (max-width: 767px) {
  .order-card {
    border-radius: 12px;
  }
}
`;

const styles = {
  page: {
    background: '#f7f7f7',
    minHeight: '100%',
    fontFamily: "'Noto Sans KR', sans-serif",

    margin: '-16px',
    padding: '16px',
  },
  topRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '12px',
    flexWrap: 'wrap',
    marginBottom: '16px',
  },
  title: {
    margin: 0,
    marginBottom: '10px',
    marginTop: '10px',
    fontSize: '24px',
    fontWeight: 700,
    color: '#212529',
  },
  shopText: {
    marginTop: '4px',
    fontSize: '13px',
    color: '#6c757d',
  },
  shopName: {
    fontWeight: 600,
  },
  filterWrap: {
    display: 'flex',
    gap: '8px',
    flexWrap: 'wrap',
    marginBottom: '10px',
  },
  filterButton: {
    display: 'inline-block',
    padding: '0.25rem 0.5rem',
    fontSize: '0.875rem',
    lineHeight: 1.5,
    color: '#6c757d',
    background: '#fff',
    border: '0px solid #6c757d',
    borderRadius: '0.25rem',
    textDecoration: 'none',
  },
  alertBox: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '8px',
    flexWrap: 'wrap',
    padding: '12px 16px',
    marginBottom: '16px',
    background: '#fff',
    border: '1px solid #dee2e6',
    borderRadius: '0.375rem',
  },
  alertLeft: {
    minHeight: '20px',
  },
  alertRight: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginLeft: 'auto',
  },
  soundPill: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    padding: '6px 10px',
    borderRadius: '999px',
    background: '#fff',
    border: '1px solid #e5e7eb',
    fontSize: '12px',
    color: '#6b7280',
    cursor: 'pointer',
  },
  soundDot: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    background: '#ef4444',
    display: 'inline-block',
  },
  soundDotOn: {
    background: '#22c55e',
  },
  emptyBox: {
    textAlign: 'center',
    color: '#6c757d',
    padding: '48px 16px',
    background: '#fff',
    border: '1px solid #dee2e6',
    borderRadius: '14px',
  },
  orderCard: {
    background: '#fff',
  },
  orderCardNew: {
    background: '#fff7f7',
  },
  cardBody: {
    padding: '16px',
  },
  cardHeaderRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: '12px',
  },
  cardHeaderLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    flexWrap: 'wrap',
  },
  cardHeaderRight: {
    textAlign: 'right',
    flexShrink: 0,
  },
  orderId: {
    fontWeight: 700,
    fontSize: '1rem',
    color: '#212529',
  },
  badge: {
    display: 'inline-block',
    padding: '0.35rem 0.55rem',
    borderRadius: '999px',
    fontSize: '13px',
    lineHeight: 1,
    whiteSpace: 'nowrap',
  },
  totalPrice: {
    fontWeight: 700,
    fontSize: '1.25rem',
    color: '#212529',
  },
  metaBlock: {
    marginTop: '6px',
    color: '#6c757d',
    fontSize: '0.875rem',
  },
  metaRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    flexWrap: 'wrap',
  },
  metaRowSecond: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    flexWrap: 'wrap',
    marginTop: '4px',
  },
  bottomRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '12px',
    marginTop: '16px',
    flexWrap: 'wrap',
  },
  actionWrap: {
    display: 'flex',
    gap: '8px',
    flexWrap: 'wrap',
  },
  actionButton: {
    padding: '0.375rem 0.75rem',
    borderRadius: '0.25rem',
    fontSize: '0.875rem',
    cursor: 'pointer',
  },
  detailButton: {
    display: 'inline-block',
    padding: '0.375rem 0.75rem',
    border: '1px solid #212529',
    color: '#212529',
    textDecoration: 'none',
    borderRadius: '0.25rem',
    fontSize: '0.875rem',
    background: '#fff',
  },
};

export default SellerLiveOrders;